import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useGoogleLogin, googleLogout } from '@react-oauth/google';
import {
  initSession,
  clearCachedSession,
  clearIdToken,
  getStoredIdToken,
  storeIdToken,
} from '../api/authApi';

const AuthContext = createContext({
  user: null,
  loading: true,
  authError: '',
  signInWithGoogle: () => {},
  signOut: () => {},
  refresh: () => {},
});

const mapSessionToUser = (sessionData) => {
  if (!sessionData) {
    return null;
  }

  if (sessionData.user) {
    return sessionData.user;
  }

  if (sessionData.user_email) {
    return {
      email: sessionData.user_email,
      name: sessionData.display_name || sessionData.name || '',
      plan: sessionData.plan,
    };
  }

  if (sessionData.email) {
    return { email: sessionData.email, name: sessionData.name };
  }

  return null;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  const applySession = useCallback((sessionData) => {
    const mapped = mapSessionToUser(sessionData);
    setUser(mapped);
  }, []);

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const session = await initSession();
      applySession(session);
    } catch (error) {
      console.warn('auth init failed', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [applySession]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    scope: 'email profile openid',
    onSuccess: async (tokenResponse) => {
      setAuthError('');
      const token = tokenResponse?.access_token || tokenResponse?.credential;
      if (!token) {
        setAuthError('Missing Google token in response.');
        return;
      }

      storeIdToken(token);
      setLoading(true);
      try {
        const session = await initSession(token);
        applySession(session);
      } catch (error) {
        console.error('Google login failed', error);
        setAuthError('Unable to complete sign in. Please try again.');
        setUser(null);
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setAuthError('Google sign-in failed. Please try again.');
    },
  });

  const signInWithGoogle = useCallback(() => {
    googleLogin();
  }, [googleLogin]);

  const revokeGoogleToken = useCallback(async () => {
    const token = getStoredIdToken();
    if (!token) {
      return;
    }
    try {
      await fetch(`https://oauth2.googleapis.com/revoke?token=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
    } catch (error) {
      console.warn('Failed to revoke Google token', error);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      await revokeGoogleToken();
      googleLogout();
      clearCachedSession();
      clearIdToken();
      setUser(null);
    } catch (error) {
      console.error('Sign out failed', error);
    } finally {
      setLoading(false);
    }
  }, [revokeGoogleToken]);

  const value = useMemo(() => ({
    user,
    loading,
    authError,
    signInWithGoogle,
    signOut,
    refresh: loadSession,
  }), [user, loading, authError, signInWithGoogle, signOut, loadSession]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
